import React, { useEffect, useMemo, useState } from 'react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  DateRangePicker,
  RangeKeyDict,
  Range,
  DateRange,
  defaultStaticRanges
} from 'react-date-range';
import dayjs, { Dayjs } from 'dayjs';
import { Box, Modal, useMediaQuery, useTheme } from '@mui/material';
import { enGB } from 'date-fns/locale';
import { Button } from '../../ui/button';

import {
  createFullRangeStaticRange,
  quarterAndYearRanges,
  weekRanges
} from './ranges';
import styles from '../CustomDateRangePicker/styles';
import useDateRange from '../../../hooks/useDateRange';

interface DateRangeCalendarProps {
  dateRange: [Dayjs, Dayjs];
  handleChangeDateRange: (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => void;
  showCalendar: boolean;
  setShowCalendar: React.Dispatch<React.SetStateAction<boolean>>;
}

function DateRangeCalendar({
  dateRange,
  handleChangeDateRange,
  showCalendar,
  setShowCalendar
}: DateRangeCalendarProps): React.ReactElement {
  const { firstDate } = useDateRange();
  const theme = useTheme();
  const isUnder1K = useMediaQuery(theme.breakpoints.down('lg'));
  const [range, setRange] = useState<RangeKeyDict | null>(null);

  const [valueDateRangePicker, setValueDateRangePicker] = useState<
    Range[] | undefined
  >([
    {
      startDate: dateRange[0]?.toDate(),
      endDate: dateRange[1]?.toDate(),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    setValueDateRangePicker([
      {
        startDate: dateRange[0]?.toDate(),
        endDate: dateRange[1]?.toDate(),
        key: 'selection'
      }
    ]);
  }, [dateRange]);

  const handleOnChange = (ranges: RangeKeyDict): void => {
    const { selection } = ranges;
    setValueDateRangePicker([selection]);
    setRange(ranges);
  };

  const combinedStaticRanges = useMemo(() => {
    const defaultRangesWithoutWeeks = defaultStaticRanges.filter(
      (defaultRange) => !defaultRange.label?.includes('Week')
    );
    const dayRanges = defaultRangesWithoutWeeks.slice(0, 2);
    const monthRanges = defaultRangesWithoutWeeks.slice(2);
    return [
      ...dayRanges,
      createFullRangeStaticRange(firstDate.toDate()),
      ...weekRanges,
      ...monthRanges,
      ...quarterAndYearRanges
    ];
  }, [firstDate]);

  return (
    <Modal
      open={showCalendar}
      onClose={(): void => setShowCalendar(false)}
      aria-labelledby='Date picker'
      aria-describedby='Choose a date range'
    >
      <Box
        sx={{
          top: '5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          position: 'absolute',
          padding: isUnder1K ? 0.5 : 2,
          borderRadius: '0.5rem',
          background: 'white'
        }}
      >
        {isUnder1K ? (
          <DateRange
            locale={enGB}
            onChange={handleOnChange}
            moveRangeOnFirstSelection={false}
            ranges={valueDateRangePicker}
            rangeColors={[theme.palette.secondary.main]}
          />
        ) : (
          <Box sx={styles.dateRangeWrapper}>
            <DateRangePicker
              locale={enGB}
              staticRanges={combinedStaticRanges}
              onChange={handleOnChange}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={valueDateRangePicker}
              direction='horizontal'
              rangeColors={[theme.palette.secondary.main]}
              inputRanges={[]}
              minDate={firstDate.toDate()}
              maxDate={new Date()}
            />
          </Box>
        )}
        <Button
          className='float-right py-1 px-4 m-1'
          aria-label='Apply range'
          onClick={(): void => {
            if (range) {
              const { selection } = range;
              if (selection.endDate && selection.startDate) {
                const start = dayjs(selection.startDate).startOf('day');
                const end = dayjs(selection.endDate).endOf('day');
                handleChangeDateRange(start, end);
                setShowCalendar(false);
              }
            }
          }}
        >
          Apply range
        </Button>
      </Box>
    </Modal>
  );
}

export default DateRangeCalendar;
